import { CaretRightIcon } from '@phosphor-icons/react'
import { shopTestId } from '@utils/utils-shop-test-ids'
import Link from 'next/link'
import type { ComponentProps, PropsWithChildren, ReactNode } from 'react'

import { cx } from '../../cx'
import { BaseButton } from '../button/button'

type BaseCardProps = {
  isFixedWidth?: boolean
  className?: string
  children: React.ReactNode
}

type CardLinkProps = BaseCardProps &
  ComponentProps<typeof Link> & {
    href: string
  }

type CardDivProps = BaseCardProps & {
  href?: undefined
}

type CardProps = CardLinkProps | CardDivProps

export function Card({ isFixedWidth, className, children, href, ...props }: CardProps) {
  const classes = cx(
    '@container group/card relative inline-block size-full',
    { 'w-64 h-auto': isFixedWidth },
    className
  )

  const content = <div className="grid min-w-0 @2xl:grid-cols-3 @2xl:gap-8 gap-3">{children}</div>

  if (href) {
    return (
      <Link href={href} className={classes} target="_blank" {...props}>
        {content}
      </Link>
    )
  }
  return <div className={classes}>{content}</div>
}

type CardImageContainerProps = PropsWithChildren<{
  className?: string
  aspectRatio?: 'video' | 'square'
}>

export function CardImageContainer({ children, className, aspectRatio }: CardImageContainerProps) {
  return (
    <div
      className={cx(
        'relative @2xl:col-span-1 @sm:aspect-auto @2xl:min-h-0 @sm:min-h-64 @xl:min-h-72 min-h-0 w-full',
        {
          'aspect-square': aspectRatio === 'square',
          'aspect-video': aspectRatio === 'video',
        },
        className
      )}
    >
      {children}
    </div>
  )
}

type CardDetailsProps = PropsWithChildren<{ className?: string }>

export function CardDetails({ children, className }: CardDetailsProps) {
  return (
    <div
      className={cx(
        '@2xl:col-span-2 flex flex-col justify-center gap-2 @2xl:py-6 @4xl:py-12',
        className
      )}
    >
      {children}
    </div>
  )
}

type CardTitleProps = {
  title: ReactNode
}

export function CardTitle({ title }: CardTitleProps) {
  return (
    <h4 className="wrap-break-word @2xl:line-clamp-2 line-clamp-3 text-balance @4xl:font-heading1 @xl:font-heading2 font-heading4">
      {title}
    </h4>
  )
}

type CardSubtitleProps = {
  subtitle: ReactNode
}

export function CardSubtitle({ subtitle }: CardSubtitleProps) {
  return (
    <p
      className="@xl:font-body-copy font-base-copy"
      data-testid={shopTestId('ExperienceSubtitle.root')}
    >
      {subtitle}
    </p>
  )
}

const cardEmptyClasses = 'grid w-64 place-items-center rounded-xl bg-lightblue'

function CardEmptyContent({ title }: { title: ReactNode }) {
  return (
    <h4 className="my-2 flex items-center gap-2 font-heading4-small xlg:font-heading4">
      {title}
      <CaretRightIcon />
    </h4>
  )
}

type CardEmptyLinkProps = Omit<ComponentProps<typeof Link>, 'children'> & {
  title: ReactNode
}

export function CardEmptyLink({ title, className, ...props }: CardEmptyLinkProps) {
  return (
    <Link className={cx(cardEmptyClasses, className)} {...props}>
      <CardEmptyContent title={title} />
    </Link>
  )
}

type CardEmptyButtonProps = Omit<ComponentProps<'button'>, 'children'> & {
  title: ReactNode
}

export function CardEmptyButton({ title, className, ...props }: CardEmptyButtonProps) {
  return (
    <BaseButton className={cx(cardEmptyClasses, className)} {...props}>
      <CardEmptyContent title={title} />
    </BaseButton>
  )
}
